<template>
  <aside
    class="pointer-events-none invisible absolute left-1/2 z-30 w-48 -translate-x-1/2 rounded-lg border border-border bg-bg-elevated/95 p-2 text-left opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
    :class="placementClass"
    role="tooltip"
  >
    <div class="flex items-center justify-between gap-2">
      <p class="truncate text-xs font-semibold text-text-primary">{{ label }}</p>
      <AppIcon v-if="movable" :name="IconName.Move" :size="14" :title="messages.popover.movableIconTitle" />
    </div>
    <p v-if="description !== null" class="mt-1 text-2xs text-text-muted">{{ description }}</p>
    <p class="mt-1 text-2xs text-text-muted">{{ playerCountLabel }}</p>
    <ul v-if="playerNames.length > 0" class="mt-1 space-y-0.5 text-2xs text-text-primary">
      <li v-for="playerName in playerNames" :key="playerName" class="truncate">{{ playerName }}</li>
    </ul>
    <p v-else class="mt-1 text-2xs text-text-muted">{{ emptyMessage }}</p>
    <p v-if="actionLabel !== null" class="mt-2 text-2xs text-accent">{{ actionLabel }}</p>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { AppIcon, IconName } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { gvgMapEn } from './locales/en.ts'
import { gvgMapRu } from './locales/ru.ts'

export interface GvgMapPointPopoverProps {
  readonly label: string
  readonly playerNames: ReadonlyArray<string>
  readonly placement?: 'above' | 'below'
  readonly description?: string | null
  readonly actionLabel?: string | null
  readonly movable?: boolean
  readonly emptyMessage?: string
}

const props = withDefaults(defineProps<GvgMapPointPopoverProps>(), {
  placement: 'below',
  description: null,
  actionLabel: null,
  movable: false,
  emptyMessage: '',
})

const messages = useLocaleMessages(defineLocaleCatalog({ en: gvgMapEn, ru: gvgMapRu }))

const placementClass = computed<string>(() =>
  props.placement === 'above' ? 'bottom-full mb-2' : 'top-full mt-2',
)
const playerCountLabel = computed<string>(() => {
  const count = props.playerNames.length
  return count === 1
    ? messages.value.popover.playerCountSingular(count)
    : messages.value.popover.playerCountPlural(count)
})
const emptyMessage = computed<string>(() =>
  props.emptyMessage === '' ? messages.value.popover.emptyRoster : props.emptyMessage,
)
</script>